import { Select } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import React, { ReactElement } from 'react';

import { LabelValuePair } from '../../utils/types';
import DebounceAjaxSelect from '../debounce_ajax_select/DebounceAjaxSelect';

interface TableColumn {
  key: string;
  title: string;
  width?: string;
}

interface TableOption extends DefaultOptionType {
  [key: string]: any;
  label: string;
  value: string | number;
}

type SelectType = {
  options?: LabelValuePair[] | TableOption[];
  value: string | undefined | number;
  error?: string;
  onChange: any;
  label: string;
  showSearch?: boolean;
  isDebounce?: boolean;
  disabled?: boolean;
  fetchOptions?: (search: string) => Promise<LabelValuePair[]>;
  placeholder?: string;
  isTable?: boolean;
  columns?: TableColumn[];
  dropdownRender?: (menu: ReactElement) => ReactElement;
  optionLabelProp?: string;
};

export default ({
  options,
  value,
  onChange,
  error,
  label,
  showSearch,
  isDebounce,
  fetchOptions,
  disabled = false,
  placeholder,
  isTable = false,
  columns = [],
  dropdownRender,
  optionLabelProp,
}: SelectType) => {
  const renderTableOption = (
    option: DefaultOptionType,
    info: { index: number },
  ) => {
    if (!isTable || !columns?.length) {
      return null;
    }

    return (
      <div
        className="c-form-select__table-option"
        style={{
          gridTemplateColumns: columns.map(() => '1fr').join(' '),
        }}
      >
        {columns.map((column) => {
          const cellValue = (option.data as any)[column.key];
          return (
            <div key={column.key} className="c-form-select__cell">
              {cellValue}
            </div>
          );
        })}
      </div>
    );
  };

  const defaultDropdownRender = (menu: ReactElement): ReactElement => {
    if (!isTable || !columns?.length) {
      return menu;
    }

    return (
      <div className="c-form-select__dropdown">
        <div
          className="c-form-select__header"
          style={{
            gridTemplateColumns: columns.map(() => '1fr').join(' '),
          }}
        >
          {columns.map((column) => (
            <div key={column.key} className="c-form-select__header-cell">
              {column.title}
            </div>
          ))}
        </div>
        <div className="c-form-select__menu-container">{menu}</div>
      </div>
    );
  };

  return (
    <div className="c-form-field">
      <div className="c-form-field__label">{label}:</div>
      <div className="c-form-field__wrapper">
        {isDebounce && fetchOptions ? (
          <DebounceAjaxSelect
            fetchOptions={fetchOptions}
            value={value || undefined}
            onChange={onChange}
            disabled={disabled}
            placeholder={placeholder}
          />
        ) : (
          <Select<any>
            options={options}
            value={value}
            onChange={onChange}
            className="c-select"
            showSearch={showSearch}
            disabled={disabled}
            placeholder={placeholder}
            optionRender={isTable ? renderTableOption : undefined}
            dropdownRender={
              dropdownRender || (isTable ? defaultDropdownRender : undefined)
            }
            style={{ width: '100%' }}
            listHeight={400}
            popupMatchSelectWidth={false}
            virtual={false}
            menuItemSelectedIcon={null}
            dropdownStyle={{ padding: 0 }}
          />
        )}
        <div className="c-form-field__error">{error || undefined}</div>
      </div>
    </div>
  );
};
